import { ScrollableRegion } from '@syncanix/ui';

import { useT } from '@/core/I18nProvider';

import { DECISION_ROWS } from './build-vs-buy-content';
import { SECTION_HEADING } from './build-vs-buy-styles';

import type { ReactElement } from 'react';

/**
 * The interpolated figures every row shares (`{engineerCost}`, `{starterPrice}`).
 *
 * An index signature rather than three named members, because `t()` takes a
 * `Record<string, string | number>` and a closed interface does not satisfy
 * one. The page builds this object from `IN_HOUSE_ANCHORS`, which is where the
 * figures are actually pinned.
 */
export type DecisionTableAnchors = Readonly<Record<string, string | number>>;

/**
 * The build-vs-buy comparison table — one row per dimension the decision turns
 * on, building on the left and buying on the right.
 *
 * A table rather than two prose columns because the reader is comparing, and
 * comparison is what a table is for: the row header names the dimension, so a
 * screen reader announces "Time to first working version" before either answer
 * and the two are heard as a pair rather than as two lists read one after the
 * other.
 *
 * It scrolls inside its own `ScrollableRegion` (three columns of prose do not
 * fit a phone), which per `ui-design.md` § 6 is also what gives the scroller a
 * tab stop — a box a mouse can pan and a keyboard cannot reach is a WCAG 2.1.1
 * failure, and `overflow-x-auto` on a bare `<div>` is exactly that box.
 */
export function DecisionTable({
  anchors,
}: {
  readonly anchors: DecisionTableAnchors;
}): ReactElement {
  const { t } = useT();
  return (
    <section aria-labelledby="bvb-decision-heading" className="mt-10">
      <h2 id="bvb-decision-heading" className={SECTION_HEADING}>
        {t('pricing.bvb.decision.heading')}
      </h2>
      <ScrollableRegion
        aria-label={t('pricing.bvb.decision.caption')}
        className="mt-4 overflow-x-auto"
      >
        <table className="w-full border-collapse text-sm">
          <caption className="sr-only">{t('pricing.bvb.decision.caption')}</caption>
          <thead>
            <tr className="border-b border-[color:var(--bd-line-3)] text-start">
              <th
                scope="col"
                className="py-2 pe-4 text-start font-semibold text-[color:var(--bd-ink)]"
              >
                {t('pricing.bvb.decision.col.dimension')}
              </th>
              <th
                scope="col"
                className="py-2 pe-4 text-start font-semibold text-[color:var(--bd-ink)]"
              >
                {t('pricing.bvb.decision.col.build')}
              </th>
              <th scope="col" className="py-2 text-start font-semibold text-[color:var(--bd-ink)]">
                {t('pricing.bvb.decision.col.buy')}
              </th>
            </tr>
          </thead>
          <tbody>
            {DECISION_ROWS.map((row) => (
              <tr
                key={row.id}
                data-decision={row.id}
                className="border-b border-[color:var(--bd-line)]"
              >
                <th
                  scope="row"
                  className="py-3 pe-4 text-start align-top font-medium text-[color:var(--bd-ink)]"
                >
                  {t(row.dimensionKey)}
                </th>
                <td className="py-3 pe-4 align-top text-[color:var(--bd-muted)]">
                  {t(row.buildKey, anchors)}
                </td>
                <td className="py-3 align-top text-[color:var(--bd-muted)]">
                  {t(row.buyKey, anchors)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </ScrollableRegion>
    </section>
  );
}
